import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { Check, Info, Send } from "../../components/icons.js";
import { CandidateTopBar } from "./CandidateChrome.js";

/**
 * Candidate · link recovery — /find-my-link (public, no account, no password).
 * A lost or expired invite link is otherwise a dead end, so this mints a fresh one and mails
 * it to the address already on the invite. NON-ENUMERATING BY DESIGN: the screen shows the
 * same neutral confirmation whether or not the email matches, so it can never answer "does
 * person X have an interview at company Y".
 */

/** The one and only outcome message — never varies with the server's answer. */
const NEUTRAL_CONFIRMATION =
  "If that email has an active interview, we've just sent the link to it. Check your inbox (and spam).";

/**
 * Email → fresh link form. Embedded both on /find-my-link and inside the invalid/expired
 * card in the candidate journey, so a broken link always offers a way forward.
 */
export function RecoverLinkForm(): ReactNode {
  const [email, setEmail] = useState("");
  const recover = useMutation({
    mutationFn: (e: string) => api.post<{ ok: boolean }>("/invite/recover", { email: e }),
  });

  // Anything the server actually answered — match or no match, throttled or not — collapses
  // to the same confirmation; only a transport/server fault is worth surfacing, and that
  // says nothing about the candidate.
  const hardFail = recover.isError && (!(recover.error instanceof ApiError) || recover.error.status >= 500);
  const sent = (recover.isSuccess || recover.isError) && !hardFail;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.includes("@") || recover.isPending) return;
    recover.mutate(email.trim());
  };

  if (sent) {
    return (
      <div className="banner info" style={{ textAlign: "left" }}>
        <Check />
        <div>
          {NEUTRAL_CONFIRMATION}
          <div className="sm" style={{ marginTop: 6, opacity: 0.85 }}>
            The new link replaces any older one, so a link from an earlier email will stop working — that's
            expected, not a fault.
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={{ textAlign: "left" }}>
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="recover-email">Email from your invitation</label>
        <input
          id="recover-email"
          className="input"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {hardFail && (
        <div className="banner crit" style={{ marginBottom: 12 }}>
          <Info />
          <span>Couldn't send that just now. Please retry in a moment.</span>
        </div>
      )}
      <button className="btn primary t1c-cta" type="submit" disabled={!email.includes("@") || recover.isPending}>
        <Send /> {recover.isPending ? "Sending…" : "Send my link"}
      </button>
    </form>
  );
}

export function FindMyLink(): ReactNode {
  return (
    <div className="cx t1c">
      <div className="cx-shell">
        <CandidateTopBar label="Verified interview" />

        <div className="cx-step">
          <h1 className="t1c-h1">Find your interview link</h1>
          <p className="mut t1c-lede">
            Enter the email your invitation was sent to and we'll send your link straight back. No account, no
            password.
          </p>
          <p className="t1c-fine" style={{ margin: "0 0 14px" }}>
            <Info /> We can only send to the address on your invitation. The new link <b>replaces any older one</b>.
          </p>
          <RecoverLinkForm />
        </div>
      </div>

      <p className="t1c-foot">
        Still stuck? Reply to the invitation message and the recruiter will reach you directly.
      </p>
    </div>
  );
}
